import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

interface Props {
  icon: LucideIcon;
  title: string;
  /**
   * What this emptiness means and what to do next.
   *
   * "No results" is almost never the useful message. In an admin tool, an
   * empty list is usually either good news (nothing to review) or a sign of a
   * misconfiguration — and those two need different copy.
   */
  body: string;
  action?: ReactNode;
}

export function EmptyState({ icon: Icon, title, body, action }: Props) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-surface-sunken text-ink-faint">
        <Icon className="h-5 w-5" strokeWidth={1.5} />
      </div>
      <p className="text-xs font-medium text-ink">{title}</p>
      <p className="mt-1 max-w-sm text-2xs leading-relaxed text-ink-muted">{body}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Row-shaped skeletons for list loading states. */
export function ListSkeleton({ rows = 5, height = 56 }: { rows?: number; height?: number }) {
  return (
    <div className="space-y-2 p-3">
      {Array.from({ length: rows }).map((_, index) => (
        <div
          key={index}
          className="animate-pulse rounded-md bg-surface-raised"
          style={{ height }}
        />
      ))}
    </div>
  );
}
